import { useEffect, useRef, useState } from "react";
import { Camera, ClipboardList, X } from "lucide-react";
import { compressPhoto, newKey } from "../../live/engagementApi";
import { planSummary } from "./PlanFormCard";

const STATUS = {
  pending_acceptance: ["En attente d’accord", "bg-[#FFF8E1] text-[#8a6500]"],
  change_requested: ["Changement demandé", "bg-[#FFF8E1] text-[#8a6500]"],
  active: ["En cours", "bg-[#E9FBF0] text-[#128a3e]"],
  completed: ["Terminé", "bg-[#0a2914] text-[#25D366]"],
  declined: ["Refusé", "bg-gray-100 text-gray-600"],
  cancelled: ["Annulé", "bg-gray-100 text-gray-600"],
  expired: ["Expiré", "bg-gray-100 text-gray-600"],
};
const DIFFICULTY = [
  ["easy", "Facile"],
  ["ok", "Ça va"],
  ["hard", "Difficile"],
];
const DIFF_LABEL = { easy: "facile", ok: "ça va", hard: "difficile" };

const Photo = ({ api, id }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    api.photoUrl(id).then((url) => alive && setSrc(url)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [api, id]);
  if (!src) return <span className="inline-block h-14 w-14 rounded-lg bg-[#EEF5F1]" />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="Preuve de la séance" className="h-14 w-14 rounded-lg object-cover" data-testid="checkin-photo" />
    </a>
  );
};

/** One session declared by the student (F5). */
const CheckinForm = ({ api, plan, realized = 0, onDone, onCancel, mobileType = false }) => {
  const size = mobileType ? "text-[15px]" : "text-[13px]";
  const head = `font-bold uppercase tracking-wider text-[#189346] ${mobileType ? "text-[12px]" : "text-[11px]"}`;
  const key = useRef(newKey());
  const planned = plan.suggestedMinutes || 20;
  const [minutes, setMinutes] = useState(realized || planned);
  const [summary, setSummary] = useState("");
  const [difficulty, setDifficulty] = useState("ok");
  const [needsHelp, setNeedsHelp] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const chip = (on) =>
    `rounded-full border px-3 py-1.5 font-semibold ${size} ${on ? "border-[#25D366] bg-[#25D366] text-[#0a2914]" : "border-gray-200 bg-white text-[#2d5a3d]"}`;

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const blob = await compressPhoto(file);
    setPhoto({ blob, url: URL.createObjectURL(blob) });
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const attachmentIds = [];
      if (photo) attachmentIds.push((await api.uploadPhoto(plan.id, photo.blob)).id);
      const result = await api.checkin(plan.id, {
        idempotencyKey: key.current,
        actualMinutes: minutes,
        summary: summary.trim(),
        difficulty,
        needsHelp,
        attachmentIds,
      });
      onDone(result.plan, { needsHelp });
    } catch (problem) {
      setError(problem.message || "Envoi impossible. Réessaie.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl bg-[#F7FAF8] p-3" data-testid="checkin-form">
      <p className={head}>Temps réalisé</p>
      {realized ? (
        <div className="mt-1 rounded-xl bg-white p-2.5" data-testid="checkin-time-result">
          <p className={`font-semibold text-[#122118] ${size}`}>
            {realized} min réalisées sur {planned} prévues
          </p>
          <p className={`mt-0.5 ${mobileType ? "text-[13px]" : "text-[11px]"} ${realized >= planned ? "text-[#189346]" : "text-[#4a5f53]"}`}>
            {realized >= planned ? "Objectif atteint, bravo !" : `${planned - realized} min de moins que prévu, c’est déjà un bon pas.`}
          </p>
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="checkin-time-pick">
          {[10, 15, 20, 30, 45].map((value) => (
            <button key={value} type="button" className={chip(minutes === value)} onClick={() => setMinutes(value)}>{value} min</button>
          ))}
        </div>
      )}
      <p className={`mt-3 ${head}`}>C’était comment ?</p>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="checkin-difficulty">
        {DIFFICULTY.map(([id, label]) => (
          <button key={id} type="button" className={chip(difficulty === id)} onClick={() => setDifficulty(id)}>{label}</button>
        ))}
      </div>
      <input
        data-testid="checkin-summary"
        value={summary}
        maxLength={500}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="Ce que j’ai fait"
        className={`mt-3 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 outline-none focus:border-[#25D366] ${size}`}
      />
      <div className="mt-3 flex items-center gap-2">
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 font-semibold text-[#2d5a3d] ${size}`}>
          <Camera className="h-4 w-4" />
          {photo ? "Changer la photo" : "Ajouter une photo"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="checkin-photo-input" />
        </label>
        {photo && (
          <span className="relative">
            <img src={photo.url} alt="" className="h-10 w-10 rounded-lg object-cover" />
            <button type="button" aria-label="Retirer la photo" onClick={() => setPhoto(null)} className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow">
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>
      <label className={`mt-3 flex items-center gap-2 text-[#122118] ${size}`}>
        <input type="checkbox" checked={needsHelp} onChange={(e) => setNeedsHelp(e.target.checked)} data-testid="checkin-needs-help" />
        J’ai besoin d’aide
      </label>
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="checkin-error">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onCancel} className={`flex-1 rounded-full border border-gray-200 bg-white px-3 py-2 font-semibold text-[#2d5a3d] ${size}`}>Annuler</button>
        <button type="button" onClick={send} disabled={busy} className={`flex-1 rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] disabled:opacity-50 ${size}`} data-testid="checkin-submit">
          {busy ? "Envoi…" : "Envoyer"}
        </button>
      </div>
    </div>
  );
};

/**
 * One engagement, live. The student answers it and declares sessions; the
 * creator sees answers, can revise after a change request, or cancel.
 */
const PlanCard = ({ plan: initial, api, viewer, viewerId, time, mobileType = false, timedMinutes = 0, onChange, onEdit }) => {
  const small = mobileType ? "text-[14px]" : "text-[12px]";
  const [plan, setPlan] = useState(initial);
  const [mode, setMode] = useState(timedMinutes ? "checkin" : "");
  const [timed, setTimed] = useState(timedMinutes);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState(null);
  const text = mobileType ? "text-[15px]" : "text-[13px]";
  const isStudent = viewer === "student";
  const isCreator = viewerId && plan.createdBy === viewerId;
  const [label, badge] = STATUS[plan.status] || [plan.status, "bg-gray-100 text-gray-600"];
  const progress = plan.progress || { done: 0, target: plan.targetCount || 1, ratio: 0 };

  const act = async (fn, event) => {
    setBusy(true);
    setError("");
    try {
      const next = await fn();
      setPlan(next);
      setMode("");
      setNote("");
      onChange?.(next, event);
    } catch (problem) {
      setError(problem.message || "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSessions = async () => {
    if (sessions) return setSessions(null);
    try {
      setSessions(await api.listCheckins(plan.id));
    } catch (problem) {
      setError(problem.message);
    }
    return undefined;
  };

  const btn = `rounded-full px-3 py-2 font-semibold whitespace-nowrap disabled:opacity-50 ${mobileType ? "text-[15px]" : "text-[13px]"}`;
  const primary = `${btn} border border-transparent bg-[#25D366] text-[#0a2914]`;
  const ghost = `${btn} border border-gray-200 bg-white text-[#2d5a3d]`;

  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid="plan-card" data-plan-status={plan.status}>
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E9FBF0] text-[#189346]">
          <ClipboardList className="h-4 w-4" />
        </span>
        <p className={`min-w-0 flex-1 font-semibold text-[#122118] ${text}`}>Plan d’étude</p>
      </div>
      <span className={`mt-2 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge}`} data-testid="plan-card-status">{label}</span>
      <p className={`mt-2 text-[#2d5a3d] ${text}`}>
        ✓ <span data-testid="plan-card-title">{plan.title}</span> · {planSummary(plan)}
      </p>
      {plan.instructions && <p className={`mt-1 ${small} text-[#4a5f53]`}>« {plan.instructions} »</p>}
      <p className={`mt-1 text-[#829C8D] ${mobileType ? "text-[13px]" : "text-[11px]"}`} data-testid="plan-card-number">
        {`Plan d’étude${plan.number ? ` #${plan.number}` : ""}`}
        {plan.originType === "self" ? " personnel" : `${plan.studentName ? ` proposé à ${plan.studentName}` : " proposé"} par ${plan.creatorName || "—"}`}
        {plan.simulated ? " · simulation" : ""}
      </p>
      {(plan.status === "active" || plan.status === "completed") && (
        <div className="mt-2" data-testid="plan-card-progress">
          <div className={`flex items-center justify-between ${small} font-semibold text-[#122118]`}>
            <span>{progress.done} sur {progress.target} {progress.target > 1 ? "séances terminées" : "séance terminée"}{plan.recurring ? " cette semaine" : ""}</span>
            {plan.declaredMinutes > 0 && <span className="font-normal text-[#4a5f53]">{plan.declaredMinutes} min déclarées</span>}
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#EEF5F1]">
            <div className="h-full rounded-full bg-[#25D366] transition-all" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
          </div>
          {plan.helpRequests > 0 && (
            <p className={`mt-1.5 font-semibold text-[#c0392b] ${small}`} data-testid="plan-card-help">Aide demandée ({plan.helpRequests})</p>
          )}
          {!isStudent && plan.lastCheckin && (
            <p className={`mt-1 ${small} text-[#4a5f53]`}>
              Dernière séance : {DIFF_LABEL[plan.lastCheckin.difficulty] || "—"}
              {plan.lastCheckin.summary ? ` · « ${plan.lastCheckin.summary} »` : ""}
            </p>
          )}
        </div>
      )}
      {plan.changeRequest?.note && (plan.status === "change_requested" || plan.status === "declined") && (
        <p className="mt-2 rounded-lg bg-[#FFF8E1] px-2 py-1.5 text-[12px] text-[#6b5400]">« {plan.changeRequest.note} »</p>
      )}

      {/* student answers */}
      {isStudent && plan.status === "pending_acceptance" && mode === "" && (
        <div className="mt-3 grid gap-2">
          <button type="button" className={primary} disabled={busy} onClick={() => act(() => api.accept(plan.id), "accepted")} data-testid="plan-accept">Accepter</button>
          <div className="flex gap-2">
            <button type="button" className={`${ghost} flex-1`} onClick={() => setMode("change")} data-testid="plan-request-change">Modifier</button>
            <button type="button" className={`${ghost} flex-1`} onClick={() => setMode("decline")} data-testid="plan-decline">Refuser</button>
          </div>
        </div>
      )}
      {(mode === "change" || mode === "decline") && (
        <div className="mt-3">
          <input
            data-testid="plan-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === "change" ? "Ce que tu aimerais changer" : "Pourquoi ? (facultatif)"}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-[#25D366]"
          />
          <div className="mt-2 flex gap-2">
            <button type="button" className={`${ghost} flex-1`} onClick={() => setMode("")}>Retour</button>
            <button
              type="button"
              className={`${primary} flex-1`}
              disabled={busy || (mode === "change" && note.trim().length < 2)}
              onClick={() => act(() => (mode === "change" ? api.requestChange(plan.id, note.trim()) : api.decline(plan.id, note.trim())), mode === "change" ? "change_requested" : "declined")}
              data-testid="plan-note-send"
            >
              Envoyer
            </button>
          </div>
        </div>
      )}
      {isStudent && plan.status === "active" && mode === "" && (
        <button type="button" className={`${primary} mt-3 w-full`} onClick={() => setMode("checkin")} data-testid="plan-checkin-open">J’ai fait une séance</button>
      )}
      {mode === "checkin" && (
        <CheckinForm
          api={api}
          plan={plan}
          realized={timed}
          mobileType={mobileType}
          onCancel={() => setMode("")}
          onDone={(next, extra) => {
            setPlan(next);
            setTimed(0);
            setMode("");
            onChange?.(next, next.status === "completed" ? "completed" : "checkin", extra);
          }}
        />
      )}

      {/* creator / parent */}
      {!isStudent && (plan.status === "active" || plan.status === "completed") && progress.done > 0 && (
        <button type="button" className={`mt-2 font-semibold text-[#128a3e] underline ${small}`} onClick={toggleSessions} data-testid="plan-sessions-toggle">
          {sessions ? "Masquer les séances" : "Voir les séances"}
        </button>
      )}
      {sessions && (
        <ul className="mt-2 space-y-2" data-testid="plan-sessions">
          {sessions.map((row, index) => (
            <li key={row.id || index} className={`rounded-lg bg-[#F7FAF8] p-2 text-[#122118] ${small}`}>
              <p>
                Séance {index + 1} · {row.actualMinutes ? `${row.actualMinutes} min sur ${plan.suggestedMinutes || 20} prévues` : "durée non indiquée"} · {DIFF_LABEL[row.difficulty] || ""}
                {row.needsHelp ? " · aide demandée" : ""}
              </p>
              {row.summary && <p className="text-[#4a5f53]">« {row.summary} »</p>}
              {row.attachmentIds?.length > 0 && (
                <div className="mt-1 flex gap-1.5">
                  {row.attachmentIds.map((id) => <Photo key={id} api={api} id={id} />)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {isCreator && plan.status === "change_requested" && (
        <button type="button" className={`${primary} mt-3 w-full`} onClick={() => onEdit?.(plan)} data-testid="plan-edit">Modifier la proposition</button>
      )}
      {isCreator && ["pending_acceptance", "change_requested", "active"].includes(plan.status) && mode === "" && (
        <button type="button" className={`${btn} mt-2 w-full border border-[#f1c7c2] bg-white text-[#c0392b] hover:bg-[#FDF2F1]`} disabled={busy} onClick={() => setMode("cancel")} data-testid="plan-cancel">
          Annuler le plan
        </button>
      )}
      {mode === "cancel" && (
        <div className="mt-2 rounded-xl bg-[#FDF2F1] p-2.5" data-testid="plan-cancel-confirm">
          <p className={`${small} font-semibold text-[#c0392b]`}>Annuler ce plan d’étude ?</p>
          <div className="mt-2 flex gap-2">
            <button type="button" className={`${ghost} flex-1`} onClick={() => setMode("")} data-testid="plan-cancel-keep">Garder</button>
            <button type="button" className={`${btn} flex-1 border border-transparent bg-[#c0392b] text-white`} disabled={busy} onClick={() => act(() => api.cancel(plan.id, ""), "cancelled")} data-testid="plan-cancel-yes">
              Oui, annuler
            </button>
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="plan-card-error">{error}</p>}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default PlanCard;
